import type { FieldErrors, FieldValues } from 'react-hook-form';
import { BlockType, DisplayLogic } from '@common/entities';

import { ConditionalElementState } from 'hooks/display-logic';
import { Block, Field } from 'types/fields';
import { Form } from 'types/form';

interface HiddenGuarantorOptions {
  form: Form;
  pageId: string;
  evaluateDisplayCondition: (displayLogic?: DisplayLogic) => ConditionalElementState;
}

export const isCountBasedGuarantorHidden = ({
  form,
  pageId,
  evaluateDisplayCondition,
}: HiddenGuarantorOptions): boolean => {
  const installmentPlanBlockIds = new Set<string>();
  form.pages.forEach((page) => {
    page.sections?.forEach((section) => {
      section.elements?.forEach((element: Block | Field) => {
        if (element.customType === BlockType.InstallmentPlans && element.id) {
          installmentPlanBlockIds.add(element.id);
        }
      });
    });
  });

  const page = form.pages.find(({ id }) => id === pageId);

  return Boolean(
    page?.sections?.some((section) =>
      section.elements?.some((element: Block | Field) => {
        if (element.customType !== BlockType.Guarantor || !element.displayLogic) return false;

        const usesInstallmentCount = element.displayLogic.displayConditions.some(
          ({ sourceBlockId }) =>
            Boolean(sourceBlockId && installmentPlanBlockIds.has(sourceBlockId)),
        );

        return (
          usesInstallmentCount &&
          evaluateDisplayCondition(element.displayLogic) === ConditionalElementState.Hidden
        );
      }),
    ),
  );
};

export const excludeHiddenGuarantorResponse = <T extends Record<string, unknown>>(
  responses: T[],
  isGuarantorHidden: boolean,
): T[] =>
  isGuarantorHidden
    ? responses.filter((response) => !Object.prototype.hasOwnProperty.call(response, 'guarantor'))
    : responses;

export const excludeHiddenGuarantorErrors = (
  errors: FieldErrors<FieldValues>,
  isGuarantorHidden: boolean,
): FieldErrors<FieldValues> => {
  if (!isGuarantorHidden) return errors;

  const visibleErrors = { ...errors };
  delete visibleErrors.guarantor;
  return visibleErrors;
};

interface SkippedHiddenGuarantorPagesOptions {
  form: Form;
  activePageIndex: number;
  targetPageIndex: number;
  evaluateDisplayCondition: (displayLogic?: DisplayLogic) => ConditionalElementState;
}

export const getSkippedHiddenGuarantorPageIndexes = ({
  form,
  activePageIndex,
  targetPageIndex,
  evaluateDisplayCondition,
}: SkippedHiddenGuarantorPagesOptions): number[] => {
  const firstSkippedIndex = Math.min(activePageIndex, targetPageIndex) + 1;
  const lastSkippedIndex = Math.max(activePageIndex, targetPageIndex);
  const skippedPageIndexes: number[] = [];

  for (let pageIndex = firstSkippedIndex; pageIndex < lastSkippedIndex; pageIndex += 1) {
    const page = form.pages[pageIndex];
    if (
      isCountBasedGuarantorHidden({
        form,
        pageId: page.id,
        evaluateDisplayCondition,
      })
    ) {
      skippedPageIndexes.push(pageIndex);
    }
  }

  return skippedPageIndexes;
};
